import { useEffect, useState } from 'react';
import { isIos, isStandalone } from './push';

/**
 * Auf den Startbildschirm legen.
 *
 * Chrome und Edge (Android, Computer) bieten ein eigenes Installationsfenster
 * an, das die Seite ausloesen darf - aber nur ueber das Ereignis
 * `beforeinstallprompt`, das sehr frueh kommt. Es wird deshalb beim Start
 * abgefangen und aufbewahrt. Safari auf dem iPhone kennt das nicht; dort
 * bleibt nur die Anleitung ueber "Teilen".
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type InstallMode =
  /** Laeuft bereits als App. */
  | 'installed'
  /** Der Browser bietet das Installationsfenster an. */
  | 'prompt'
  /** iPhone/iPad in Safari: Anleitung. */
  | 'ios'
  /** Sonstige Browser: ueber das Browsermenue. */
  | 'manual';

let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const changed = (): void => listeners.forEach((listener) => listener());

/** Einmal beim Start aufrufen, bevor der Browser das Ereignis verschickt. */
export function initInstall(): void {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferred = event as BeforeInstallPromptEvent;
    changed();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    installed = true;
    changed();
  });
}

function currentMode(): InstallMode {
  if (installed || isStandalone()) return 'installed';
  if (deferred) return 'prompt';
  if (isIos()) return 'ios';
  return 'manual';
}

export function useInstall(): { mode: InstallMode; install: () => Promise<boolean> } {
  const [mode, setMode] = useState<InstallMode>(currentMode);
  useEffect(() => {
    const update = (): void => setMode(currentMode());
    listeners.add(update);
    update();
    return () => { listeners.delete(update); };
  }, []);
  return {
    mode,
    install: async () => {
      const event = deferred;
      if (!event) return false;
      // Ein Ereignis laesst sich nur einmal verwenden.
      deferred = null;
      await event.prompt();
      const choice = await event.userChoice;
      changed();
      return choice.outcome === 'accepted';
    },
  };
}
